import { useState } from 'preact/hooks';
import { stockRows, type ReturnEntry } from '@khata/core';
import {
  Empty,
  MonthPicker,
  PageHead,
  Row,
  SearchBox,
  ShowMore,
  VoidedToggle,
} from '../components/index.js';
import { dShort, qty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { itemName, partyName, returnReason } from '../lib/labels.js';
import { listEntries, matches, monthsWithEntries, periodRange } from '../lib/lists.js';
import { usePaged } from '../lib/paging.js';
import { listPeriod, openSheet, showVoided } from '../lib/ui.js';

export function Returns() {
  const s = state.value;
  const m = master.value;
  const [q, setQ] = useState('');
  const defects = stockRows(s, m, true).filter((r) => r.defQty > 0);
  const defectCount = defects.reduce((tt, r) => tt + r.defQty, 0);
  const range = periodRange(listPeriod.value, today.value);
  const inPeriod = listEntries(
    s,
    entries.value,
    (e): e is ReturnEntry => e.type === 'return',
    showVoided.value,
    range,
  );
  const rows = q.trim()
    ? inPeriod.filter(({ entry: e }) =>
        matches(
          `${itemName(m, e.itemId)} ${partyName(m, e.partyId)} ${returnReason(e.reason)} ${e.note}`,
          q,
        ),
      )
    : inPeriod;
  const paged = usePaged(rows, listPeriod.value.kind === 'all' ? 300 : 1000);

  return (
    <>
      <PageHead title={t('returns_title')} sub={t('returns_sub', { n: qty(defectCount as never) })}>
        <button type="button" class="btn btn-primary" onClick={() => openSheet({ kind: 'return' })}>
          {t('returns_add')}
        </button>
      </PageHead>
      {defects.length > 0 && (
        <div class="chips">
          {defects.map((r) => (
            <button
              key={r.item.id}
              type="button"
              class="tag tag-neutral clickable"
              title={t('returns_binHint')}
              onClick={() => openSheet({ kind: 'return', itemId: r.item.id })}
            >
              {r.item.name} · {qty(r.defQty)} {r.item.unit}
            </button>
          ))}
        </div>
      )}
      <SearchBox value={q} onInput={setQ} placeholder={t('list_search')} />
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <VoidedToggle />
      <div class="list">
        {rows.length === 0 && <Empty>{t('returns_empty')}</Empty>}
        {paged.visible.map(({ entry: e, voided }) => (
          <Row
            key={e.id}
            title={`${itemName(m, e.itemId)} ×${qty(e.qty)}`}
            sub={`${dShort(e.date)} · ${e.kind === 'sale' ? `${t('returns_fromCustomer')} ${partyName(m, e.partyId)}` : `${t('returns_toVendor')} ${partyName(m, e.partyId, t('noVendor'))}`} · ${returnReason(e.reason)}`}
            amount={taka(e.amount)}
            amountTone={voided ? 'muted' : 'ink'}
            amountSub={voided ? t('voided') : undefined}
            strike={voided}
            onClick={() => openSheet({ kind: 'entry', id: e.id })}
          />
        ))}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
